import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container } from '@mui/material';
import Header from './components/Header';
import DiveExplorer from './components/DiveExplorer';
import AuthCallback from './components/AuthCallback';
import ResetPassword from './components/ResetPassword';
import Unsubscribe from './components/Unsubscribe';
import ConfirmEmailChange from './components/ConfirmEmailChange';
import DiveDetail from './components/DiveDetail';
import DiveSharedClaim from './components/DiveSharedClaim';
import EquipmentManagement from './components/EquipmentManagement';
import Flashcards from './components/Flashcards';
import DiveSiteDetail from './components/DiveSiteDetail';
import DiveSiteListView from './components/DiveSiteListView';
import DiveCalendar from './components/DiveCalendar';
import DiveBuddies from './components/DiveBuddies';
import DiscoverDivers from './components/DiscoverDivers';
import Messages from './components/Messages';
import { DiveGroupsList, DiveGroupDetail } from './components/DiveGroups';
import { DiveClubsList, DiveClubDetail } from './components/DiveClubs';
import { DiveTripsList, DiveTripDetail } from './components/DiveTrips';
import UserProfile from './components/UserProfile';
import FeedPage from './components/FeedPage';
import PostDetail from './components/PostDetail';
import DiveCalendarJoin from './components/DiveCalendarJoin';
import BreathCoreTraining from './components/BreathCoreTraining';
import ApneaTraining from './components/ApneaTraining';
import DisclaimerDialog from './components/DisclaimerDialog';
import SuspensionNotice from './components/SuspensionNotice';
import ErrorBoundary from './components/ErrorBoundary';
import ChatPage from './components/ChatPage';
import ChatWidget from './components/ChatWidget';
import AdminPanel from './components/AdminPanel';
import { OptionsManager, getEffectiveTheme } from './utils/optionsManager';
import { NotificationProvider } from './components/NotificationContext';
import { authService } from './services/authService';

// Initialize demo data exporter (adds global function to window)
import './utils/demoDataExporter';
import OnboardingGate from './components/OnboardingGate';
import AppFooter from './components/AppFooter';
import CommunitiesPage from './components/CommunitiesPage';
import RegionCommunity from './components/RegionCommunity';
import CountryCommunity from './components/CountryCommunity';
import InviteLanding from './components/InviteLanding';
import TagInviteLanding from './components/TagInviteLanding';
import TagInviteHost from './components/TagInviteHost';
import HomeRoute from './components/HomeRoute';
import NativeUrlHandler from './components/NativeUrlHandler';
import RegionIndexSync from './components/RegionIndexSync';

// ─── Magenta palette (light & dark) ─────────────────────────────────────────
const MAG = {
  primary:      '#D4186A',
  primaryLight: '#F03C8E',
  primaryDark:  '#A0104C',
  secondary:    '#D4A843',
  secondaryLight:'#F0C866',
  secondaryDark: '#B8891A',
  darkBg:       '#120A10',
  darkSurface:  '#1E101A',
  darkPaper:    '#2A1622',
  lightBg:      '#FDF0F5',
  lightPaper:   '#FFF4F8',
};

// ─── Ocean palette (light & dark) ────────────────────────────────────────────
const OCN = {
  primary:      '#0097A7',  // teal
  primaryLight: '#00BCD4',
  primaryDark:  '#006978',
  secondary:    '#43A047',  // green
  secondaryLight:'#76D275',
  secondaryDark: '#2E7D32',
  accent:       '#546E7A',  // blue-grey
  darkBg:       '#07121A',
  darkSurface:  '#0D1E28',
  darkPaper:    '#112536',
  lightBg:      '#EEF6F8',
  lightPaper:   '#F5FBFC',
};

const buildTheme = (
  p: typeof MAG | typeof OCN,
  isDark: boolean,
  bodyGradientDark: string,
  bodyGradientLight: string,
  appBarDark: string,
  appBarLight: string,
  borderColorDark: string,
  borderColorLight: string,
  textBodyDark: string,
  textBodyLight: string,
  textSecondDark: string,
  textSecondLight: string,
) => createTheme({
  palette: {
    mode: isDark ? 'dark' : 'light',
    primary: {
      main:          isDark ? p.primary      : p.primaryDark,
      light:         isDark ? p.primaryLight : p.primary,
      dark:          isDark ? p.primaryDark  : (p as any).primaryDarker ?? p.primaryDark,
      contrastText:  '#FFFFFF',
    },
    secondary: {
      main:          isDark ? p.secondary      : p.secondaryDark,
      light:         isDark ? p.secondaryLight : p.secondary,
      dark:          isDark ? p.secondaryDark  : (p as any).secondaryDarker ?? p.secondaryDark,
      contrastText:  isDark ? '#0D0D0D' : '#FFFFFF',
    },
    success: { main: '#4CAF50', light: '#81C784', dark: '#2E7D2F' },
    background: {
      default: isDark ? p.darkBg    : p.lightBg,
      paper:   isDark ? p.darkPaper : p.lightPaper,
    },
    text: {
      primary:   isDark ? textBodyDark   : textBodyLight,
      secondary: isDark ? textSecondDark : textSecondLight,
    },
    divider: isDark ? borderColorDark : borderColorLight,
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800 }, h2: { fontWeight: 800 },
    h3: { fontWeight: 700 }, h4: { fontWeight: 700 },
    h5: { fontWeight: 700 }, h6: { fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: isDark ? bodyGradientDark : bodyGradientLight,
          minHeight: '100vh',
          overflowX: 'hidden',
        },
        ...(isDark && {
          '[style*="background-color: #e3f2fd"], [style*="backgroundColor: #e3f2fd"]': { backgroundColor: `${p.darkSurface} !important` },
          '[style*="background-color: #f3e5f5"], [style*="backgroundColor: #f3e5f5"]': { backgroundColor: `${p.darkSurface} !important` },
          '[style*="background-color: #e8f5e8"], [style*="backgroundColor: #e8f5e8"]': { backgroundColor: `${p.darkSurface} !important` },
          '[style*="background-color: #e8f4fd"], [style*="backgroundColor: #e8f4fd"]': { backgroundColor: `${p.darkSurface} !important` },
          '[style*="background-color: #f5f5f5"], [style*="backgroundColor: #f5f5f5"]': { backgroundColor: `${p.darkSurface} !important` },
          '[style*="background-color: #fff3e0"], [style*="backgroundColor: #fff3e0"]': { backgroundColor: `${p.darkSurface} !important` },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: isDark ? appBarDark : appBarLight,
          borderBottom: `1px solid ${isDark ? borderColorDark : borderColorLight}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? p.darkPaper : p.lightPaper,
          backgroundImage: 'none',
          backdropFilter: !isDark ? 'blur(8px)' : 'none',
          border: `1px solid ${isDark ? borderColorDark : borderColorLight}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: isDark
            ? `linear-gradient(145deg, ${p.darkPaper}, ${p.darkSurface})`
            : `linear-gradient(145deg, ${p.lightPaper}, ${p.lightBg})`,
          border: `1px solid ${isDark ? borderColorDark : borderColorLight}`,
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: isDark
            ? `linear-gradient(135deg, ${p.primaryDark}, ${p.primary})`
            : `linear-gradient(135deg, ${p.primaryDark}, ${p.primary})`,
          color: '#FFFFFF',
          fontWeight: 700,
          '&:hover': {
            background: isDark
              ? `linear-gradient(135deg, ${p.primary}, ${p.primaryLight})`
              : `linear-gradient(135deg, ${p.primary}, ${p.primaryLight})`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${p.secondaryDark}, ${p.secondary})`,
          color: '#FFFFFF',
          fontWeight: 700,
          '&:hover': {
            background: `linear-gradient(135deg, ${p.secondary}, ${p.secondaryLight})`,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          '&.Mui-selected': { color: isDark ? p.primaryLight : p.primaryDark },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: isDark ? p.primary : p.primaryDark,
          height: 3,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? `${p.primary}2E` : `${p.primaryDark}1F`,
          color: isDark ? p.primaryLight : p.primaryDark,
          '&.MuiChip-colorSuccess': {
            backgroundColor: isDark ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.1)',
            color: isDark ? '#81C784' : '#2E7D2F',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: isDark ? `${p.secondary}2E` : `${p.secondaryDark}1F`,
            color: isDark ? p.secondaryLight : p.secondaryDark,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: isDark ? `${p.primary}2E` : `${p.primaryDark}1F`,
            '&:hover': { backgroundColor: isDark ? `${p.primary}42` : `${p.primaryDark}2E` },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          ...(isDark && {
            '&.MuiTypography-body1':    { color: textBodyDark,   fontWeight: 400 },
            '&.MuiTypography-body2':    { color: textSecondDark, fontWeight: 400 },
            '&.MuiTypography-h6':       { color: textBodyDark,   fontWeight: 700 },
            '&.MuiTypography-subtitle1':{ color: textBodyDark,   fontWeight: 500 },
            '&.MuiTypography-subtitle2':{ color: textSecondDark, fontWeight: 500 },
          }),
        },
      },
    },
  },
});

const createAppTheme = (mode: 'light' | 'dark' | 'aiDiveBotLight' | 'aiDiveBotDark') => {
  const isOcean = mode === 'aiDiveBotLight' || mode === 'aiDiveBotDark';
  const isDark  = mode === 'dark'           || mode === 'aiDiveBotDark';

  if (isOcean) {
    return buildTheme(
      OCN, isDark,
      // body gradients
      `radial-gradient(ellipse at top, #0D2535 0%, ${OCN.darkBg} 65%)`,
      `radial-gradient(ellipse at top, #C8EAF0 0%, ${OCN.lightBg} 65%)`,
      // appbar
      `linear-gradient(135deg, #041018 0%, #082030 50%, #041018 100%)`,
      `linear-gradient(135deg, #042A38 0%, #085070 50%, #042A38 100%)`,
      // divider / border
      'rgba(0,188,212,0.25)', 'rgba(0,105,120,0.22)',
      // text
      '#D0EEF2', '#0A2830',
      '#7ABFC8', '#1A5060',
    );
  }

  return buildTheme(
    MAG, isDark,
    // body gradients
    `radial-gradient(ellipse at top, #2A1020 0%, ${MAG.darkBg} 65%)`,
    `radial-gradient(ellipse at top, #FFD6E8 0%, ${MAG.lightBg} 65%)`,
    // appbar
    `linear-gradient(135deg, #0D0008 0%, #1E0018 50%, #0D0008 100%)`,
    `linear-gradient(135deg, #1A0010 0%, #3D0028 50%, #1A0010 100%)`,
    // divider / border
    'rgba(212,24,106,0.28)', 'rgba(160,16,76,0.2)',
    // text
    '#F5E0EC', '#1A0010',
    '#C490A8', '#6B2040',
  );
};

function App() {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark' | 'aiDiveBotLight' | 'aiDiveBotDark'>(() => {
    const optionsManager = OptionsManager.getInstance();
    return getEffectiveTheme(optionsManager.getOption('display').theme);
  });
  // Null until DisclaimerDialog reports its state, so page content never flashes
  // underneath the disclaimer modal (e.g. a sign-in prompt stacking behind it).
  const [disclaimerPending, setDisclaimerPending] = useState<boolean | null>(null);

  useEffect(() => {
    // Refresh the cached user from the server so admin status is always current
    if (authService.isAuthenticated()) {
      authService.refreshUser();
    }
  }, []);

  useEffect(() => {
    const optionsManager = OptionsManager.getInstance();

    // Subscribe to theme changes
    const unsubscribe = optionsManager.subscribe((options) => {
      const newTheme = getEffectiveTheme(options.display.theme);
      setCurrentTheme(newTheme);
    });

    // Listen for system theme changes when in auto mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      const currentOptions = optionsManager.getOption('display');
      if (currentOptions.theme === 'auto' || currentOptions.theme === 'aiDiveBotAuto') {
        setCurrentTheme(getEffectiveTheme(currentOptions.theme));
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      unsubscribe();
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  useEffect(() => {
    const isDark = currentTheme === 'dark' || currentTheme === 'aiDiveBotDark';
    const isOcean = currentTheme.startsWith('aiDiveBot');

    // The favicon is the same NAS logo as the home-screen icon, recolored per
    // palette. Recreate the link node each time — in-place href updates don't
    // reliably make browsers refresh the tab icon.
    document.getElementById('app-favicon')?.remove();
    const favicon = document.createElement('link');
    favicon.id = 'app-favicon';
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    favicon.setAttribute('sizes', '128x128');
    favicon.href = `${process.env.PUBLIC_URL}/favicon-nas${isOcean ? '-ocean' : ''}.png`;
    document.head.appendChild(favicon);

    const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeColorMeta) {
      const palette = isOcean ? OCN : MAG;
      themeColorMeta.content = isDark ? palette.darkBg : palette.primary;
    }
  }, [currentTheme]);

  const theme = createAppTheme(currentTheme);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <Router>
          <NativeUrlHandler />
          <RegionIndexSync />
          <Header />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ErrorBoundary>
              {disclaimerPending !== false ? null : (
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/invite/:inviterUserId" element={<InviteLanding />} />
                <Route path="/tag/:postId/:tagId/:code" element={<TagInviteLanding />} />
                <Route path="/calendar" element={<DiveCalendar />} />
                <Route path="/calendar/join/:inviteCode" element={<DiveCalendarJoin />} />
                <Route path="/trips" element={<DiveTripsList />} />
                <Route path="/trips/:tripId" element={<DiveTripDetail />} />
                <Route path="/explore" element={<DiveExplorer />} />
                <Route path="/buddies" element={<DiveBuddies />} />
                <Route path="/communities" element={<CommunitiesPage />} />
                <Route path="/communities/:slug" element={<RegionCommunity />} />
                <Route path="/countries/:slug" element={<CountryCommunity />} />
                <Route path="/discover-divers" element={<DiscoverDivers />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/groups" element={<DiveGroupsList />} />
                <Route path="/groups/:groupId" element={<DiveGroupDetail />} />
                <Route path="/clubs" element={<DiveClubsList />} />
                <Route path="/clubs/:clubId" element={<DiveClubDetail />} />
                <Route path="/feed" element={<FeedPage />} />
                <Route path="/post/:postId" element={<PostDetail />} />
                <Route path="/u/:userId" element={<UserProfile />} />
                {/* Legacy redirects */}
                <Route path="/dive-log" element={<Navigate to="/explore?tab=log" replace />} />
                <Route path="/dive-sites" element={<Navigate to="/explore?tab=sites" replace />} />
                <Route path="/dive-lists" element={<Navigate to="/explore?tab=lists" replace />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/confirm-email" element={<ConfirmEmailChange />} />
                {/* Static segment first, mirroring /dive-site-lists/shared/:shareCode */}
                <Route path="/dive/shared/:shareCode" element={<DiveSharedClaim />} />
                <Route path="/dive/:diveId" element={<DiveDetail />} />
                <Route path="/equipment" element={<EquipmentManagement />} />
                <Route path="/training" element={<BreathCoreTraining />} />
                <Route path="/flashcards" element={<Flashcards />} />
                <Route path="/apnea" element={<ApneaTraining />} />
                <Route path="/dive-site-lists/shared/:shareCode" element={<DiveSiteListView />} />
                <Route path="/dive-site-lists/:listId" element={<DiveSiteListView />} />
                <Route path="/dive-sites/:slug" element={<DiveSiteDetail />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/admin" element={<AdminPanel />} />
              </Routes>
              )}
            </ErrorBoundary>
          </Container>

          <AppFooter />

          {/* Disclaimer dialog - shows on first visit. Reports pending state so page
              content (e.g. a sign-in prompt) never renders stacked underneath it. */}
          <DisclaimerDialog onPendingChange={setDisclaimerPending} />
          <SuspensionNotice />
          <OnboardingGate disclaimerPending={disclaimerPending} />
          <TagInviteHost />

          {/* Nereus floating chat widget */}
          <ChatWidget />
        </Router>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;